import React, { useReducer, useEffect, useState, useLayoutEffect } from "react";
import StackGrid from "react-stack-grid";
import client from "@culturehq/client";
import { Loader, Icon } from "@culturehq/components";
import { Trans, getName } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import StoryThumbnail from "components/common/StoryThumbnail";
import FeedItemPlaceholder from "components/feed/FeedItemPlaceholder";
import LightboxStories from "components/common/gallery/LightboxStories";
import TrendingStories from "components/stories/TrendingStories";
import { useLocation } from "react-router-dom";
import PostStory from "components/stories/post-stories-v2/PostStory";

const initialState = {
  appending: null,
  appendingImages: false,
  error: null,
  fetching: false,
  pagination: {},
  stories: []
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return {
        ...state,
        appending: state.pagination.currentPage + 1,
        appendingImages: true
      };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        stories: action.response.stories
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "storyUpdate":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, ...action.updates };
          }
          return story;
        })
      };
    case "storyRemove":
      return {
        ...state,
        stories: state.stories.filter(story => story.id !== action.storyId)
      };
    case "storyCommentsUpdated":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, commentCount: action.numberOfComments };
          }
          return story;
        })
      };
    default:
      return state;
  }
};

const useFetchStories = (dispatch, user) => useEffect(() => {
  let cancelled = false;
  dispatch({ type: "fetchStart" });

  client
    .listStories({ userId: user.id })
    .then(response => {
      if (!cancelled) {
        dispatch({ type: "fetchSuccess", response });
      }
    })
    .catch(error => {
      if (!cancelled) {
        dispatch({ type: "fetchError", error });
      }
    });

  return () => {
    cancelled = true;
  };
}, [dispatch, user]);

const useAppendStories = (dispatch, appending, user) => useEffect(() => {
  if (appending) {
    let cancelled = false;

    client
      .listStories({ page: appending, userId: user.id })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "appendSuccess", response });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "appendError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  }

  return undefined;
}, [dispatch, appending, user]);

const useScrollListen = (dispatch, feedRef, pagination, stories) => useEffect(() => {
  if (pagination.currentPage !== pagination.totalPages) {
    const onScroll = () => {
      const feedBottom = feedRef.current.offsetTop + feedRef.current.offsetHeight;
      const windowBottom = window.pageYOffset + window.innerHeight;

      if (feedBottom - windowBottom < 100 && stories.length !== 0) {
        dispatch({ type: "appendStart" });
      }
    };

    document.addEventListener("scroll", onScroll);
    return () => document.removeEventListener("scroll", onScroll);
  }

  return undefined;
}, [dispatch, feedRef, pagination, stories]);

const getColumnWidth = storiesNumber => {
  if (window.innerWidth >= 768 && storiesNumber !== 1) {
    return "50%";
  }

  return "100%";
};

const ProfileStories = ({ feedRef, isProfile, user }) => {
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(undefined);
  const [activeStoryQuestion, setActiveStoryQuestion] = useState(null);
  const [openShareStoryModal, setOpenShareStoryModal] = useState(null);
  const [columnWidth, setColumnWidth] = useState(getColumnWidth());

  useFetchStories(dispatch, user);
  useAppendStories(dispatch, state.appending, user);
  useScrollListen(dispatch, feedRef, state.pagination, state.stories);

  useLayoutEffect(() => {
    const updateSize = () => {
      setColumnWidth(getColumnWidth(state.stories.length));
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [state.stories.length]);

  useEffect(() => {
    setColumnWidth(getColumnWidth(state.stories.length));
  }, [state.stories.length]);

  useEffect(() => {
    const getStoryQuestion = async storyQuestionId => {
      const { storyQuestion } = await client.getStoryQuestion({
        storyQuestionId
      });
      setActiveStoryQuestion(storyQuestion);
    };

    const newAction = path => {
      switch (path) {
        case "create":
          setOpenShareStoryModal(true);
          break;
        default:
          getStoryQuestion(path);
          break;
      }
    };

    const paths = location.pathname.split("/");
    switch (paths[2]) {
      case "new":
        newAction(paths[4]);
        break;
      default:
        setOpenShareStoryModal(null);
        setActiveStoryQuestion(null);
        break;
    }
  }, [location.pathname]);

  if (state.fetching) {
    return (
      <>
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
      </>
    );
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }

  const onStoryUpdate = updatedStory => {
    dispatch({
      type: "storyUpdate",
      storyId: updatedStory.id,
      updates: updatedStory
    });
    setActiveStory(updatedStory);
  };

  const onStoryRemove = storyId => {
    setModalIsOpen(false);
    dispatch({ type: "storyRemove", storyId });
  };

  const handleThumbnailClick = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = state.stories.length - 1;
    }

    if (goToIndex > state.stories.length - 1) {
      target = 0;
    }

    setActiveStory(state.stories[target]);
    setModalIsOpen(true);
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    state.stories = state.stories.map(story => (
      story.id !== storyId ? story : { ...story, cheerForUser, cheersCount })
    );
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    dispatch({
      type: "storyCommentsUpdated",
      storyId: story.id,
      numberOfComments
    });
  };

  return (
    <>
      {isProfile && <TrendingStories openModal isProfile />}
      <div className="Stories__container">
        {activeStoryQuestion && (
          <PostStory
            initialOpen
            preFilledQuestion={activeStoryQuestion}
            path="/profile"
          />
        )}
        {openShareStoryModal && <PostStory initialOpen path="/profile" />}
        {activeStory && (
          <LightboxStories
            modalIsOpen={modalIsOpen}
            activeStory={activeStory}
            currentUserAnswered
            stories={state.stories}
            onCommentsChange={handleCommentsChange}
            onClose={handleClose}
            onStoryChange={handleThumbnailClick}
            onDelete={onStoryRemove}
            onStoryCheerToggle={handleStoryCheerToggle}
            onStoryUpdate={onStoryUpdate}
          />
        )}
        <div className="Stories__title-container">
          <h2 className="Stories__title-container__title">
            {isProfile ? (
              <Trans>My Stories</Trans>
            ) : (
              <Trans>Get to know {getName(user)}</Trans>
            )}
          </h2>
          {state.stories.length > 0 && (
            <button
              type="submit"
              className="Stories__title-container__gallery"
              onClick={() => {
                handleThumbnailClick(0);
              }}
            >
              <Icon icon="images" />
              <Trans>View in gallery mode</Trans>
            </button>
          )}
        </div>
        <div className="Stories__container--images">
          <StackGrid columnWidth={columnWidth} monitorImagesLoaded>
            {state.stories.map((story, index) => (
              <ErrorBoundary key={`${story.id}-${index}`}>
                <StoryThumbnail
                  index={index}
                  key={`${story.id}-${index}`}
                  story={story}
                  onClick={handleThumbnailClick}
                  showCreator={false}
                  showTags
                  showQuestion
                />
              </ErrorBoundary>
            ))}
          </StackGrid>
          {state.appending && <Loader loading />}
          {state.stories.length < 1 && (
            <div className="EmptyResults EmptyResults-feed">
              <p>
                <Trans>No stories to show...</Trans>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileStories;
